import api from './api-service';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import FlagService from './flag-service';
import UserService from '~/modules/services/user-service';

const STORE_KEY = 'pixwel.api.credentials';
const CLERK_STORE_KEY = '__session';

/**
 * Auth error message constants used when session resume/login fails.
 * Exported so other modules (e.g. route-service) can reference them
 * without duplicating magic strings.
 */
export const AUTH_ERRORS = {
	NO_TOKEN: 'no-token',
	TOKEN_EXPIRED: 'token-expired',
	ACCESS_DENIED: 'access-denied'
};

const AuthService = {

	_persistSessionToken: (session, username) => {
		if(session?.token) {
			AuthService.setToken({
				username: session.emailNormal || username,
				token: session.token
			});
		}
	},

	_configureAuthenticatedSession: (session) => {
		if( !session?._id ) {
			return session;
		}

		// Identify in every environment so person-targeted feature flags resolve
		// locally too. PostHog analytics capture is suppressed outside
		// production/staging in FlagService.init(), so this adds no dev noise.
		FlagService.identify(session);

		LogRocket?.identify(session._id, {
			name: session.name,
			email: session.emailNormal,
			isImpersonating: AuthService.getToken().isImpersonating || false
		});
		const isAdmin = UserService.isAdmin(session);
		const isImpersonating = AuthService.isImpersonating();
		if( Sentry.getCurrentHub().getClient() ) {
			Sentry.getCurrentHub().getClient()
				.getOptions().enabled = (window.pixwel?.env !== 'development' && (isAdmin || isImpersonating));
		}
		LogRocket.getSessionURL((sessionURL) => {
			document.cookie = `LogRocket=${sessionURL}`;
			Sentry.getCurrentScope().setExtra('sessionURL', sessionURL);
		});

		Sentry.getCurrentScope().setUser({
			email: session.emailNormal,
			username: session.name
		});

		return session;
	},

	login: (credentials) => {

		const isToken = !!credentials.token;
		const isTwoFactor = credentials.isTwoFactor;

		if(isToken && isTwoFactor) {
			AuthService.setToken({
				username: credentials.username,
				token: credentials.token
			});
		}

		credentials = !isToken
			? credentials
			: {
				username: credentials.username,
				password: 'token-' + credentials.token
			};

		const url = `${window.location.origin}/login/:token`;
		const params = {};
		if(!isTwoFactor) {
			params.url = url;
		}

		// Somebody typing a password, rather than a token being replayed.
		const isPassword = !isToken && !isTwoFactor;

		return api.client.get('/session', {
			params,
			auth: credentials,
			// The shared interceptor reads any 401 as an expired session: toast, drop the token,
			// reload the page. Right for a token that has genuinely expired, wrong for a
			// mistyped password — the caller never sees a rejection (the interceptor returns a
			// promise that never settles), so the form sits disabled and the page reloads
			// instead of saying what went wrong. `twoFactorLogin` opts out for the same reason.
			// The token path keeps the old behaviour deliberately.
			...(isPassword ? { validateStatus: () => true } : {})
		})
			.then(({ data: session, status } = {}) => {
				if(isPassword && status !== 200) {
					// `Session::view()` answers a wrong password with a bare 401 and no body, so
					// there is usually nothing to quote — and `AUTH_ERRORS.ACCESS_DENIED` is the
					// slug `access-denied`, which is not a sentence to show anybody.
					const failure = new Error(status >= 500
						? 'Sign-in is not available right now'
						: (session?.error || 'That email address and password do not match'));

					// Carried on the error because `validateStatus` stops axios rejecting, so
					// nothing else will attach it — and `handleError` skips its Sentry capture
					// and its global toast on a 401 or 403. Without this a mistyped password
					// raised an exception report and a red banner on top of the message already
					// sitting inline on the form.
					failure.status = status;

					// The caller shows this sentence inline, so the login mutation's `onError`
					// must not hand it to `handleError` as well: that only skips 401–422, and a
					// 503 (the sealing key that does not open the credentials) toasted and filed
					// a Sentry report on top of the message already on the form.
					failure.surfaced = true;

					throw failure;
				}

				if(!session) {
					throw new Error(isToken ? AUTH_ERRORS.TOKEN_EXPIRED : AUTH_ERRORS.ACCESS_DENIED);
				}

				// 2FA intermediate response (e.g. { twofactor: true }) - return early without persisting
				if( !session._id ) {
					return session;
				}

				AuthService._persistSessionToken(session, credentials.username);

				return AuthService._configureAuthenticatedSession(session);
			});
	},

	/**
	 * Exchanges a Google authorization code for an ordinary Pixwel session.
	 *
	 * The API redeems the code with Google, matches the resulting ID token's verified address
	 * against a user record, and mints the same session token a password login gets — so from
	 * here on nothing downstream (resume, impersonation, Aspera, logout) can tell them apart.
	 *
	 * @param {Object} params
	 * @param {string} params.code - the one-time code from Google's popup
	 * @returns {Promise<Object>} the session
	 */
	googleLogin: ({ code }) => {
		return api.client.post('/session/google', { code }, {
			// The shared response interceptor treats any 401 as an expired session: it clears
			// the cookie, toasts "please try logging in again" and reloads. That would wipe a
			// sign-in error before anyone could read it, so a refusal is read from the status
			// here instead of being thrown at the interceptor.
			validateStatus: () => true
		})
			.then(({ data, status } = {}) => {
				if(status !== 200 || !data?.token) {
					// A refusal carries a reason worth repeating; a 5xx carries an error code
					// meant for our logs (`sso_auth_unconfigured`), not for the person.
					throw new Error(status >= 500
						? 'Google sign-in is not available right now'
						: (data?.error || AUTH_ERRORS.ACCESS_DENIED));
				}

				AuthService._persistSessionToken(data, data.emailNormal);

				return AuthService._configureAuthenticatedSession(data);
			});
	},

	/**
	 * Exchanges a Microsoft authorization code for an ordinary Pixwel session.
	 *
	 * The redirect URI travels with the code because OAuth requires the redemption to quote the
	 * same one the popup returned to, and which that is depends on the origin this bundle was
	 * served from. The API checks it against its own registered list before using it.
	 *
	 * @param {Object} params
	 * @param {string} params.code - the one-time code from Microsoft's popup
	 * @param {string} params.redirectUri - the URI that popup returned to
	 * @param {string} params.codeVerifier - the PKCE verifier the code is paired with
	 * @returns {Promise<Object>} the session
	 */
	microsoftLogin: ({ code, redirectUri, codeVerifier }) => {
		return api.client.post('/session/microsoft', { code, redirectUri, codeVerifier }, {
			// Same reason as the Google path: the shared interceptor would turn a refusal into
			// "session expired" plus a reload, wiping the message before anyone could read it.
			validateStatus: () => true
		})
			.then(({ data, status } = {}) => {
				if(status !== 200 || !data?.token) {
					throw new Error(status >= 500
						? 'Microsoft sign-in is not available right now'
						: (data?.error || AUTH_ERRORS.ACCESS_DENIED));
				}

				AuthService._persistSessionToken(data, data.emailNormal);

				return AuthService._configureAuthenticatedSession(data);
			});
	},

	guestLogin: ({ username, token }) => {
		return api.client.post('/session/guest', { username, token })
			.then(({ data: session } = {}) => {
				if(!session || !session.token) {
					throw new Error(AUTH_ERRORS.ACCESS_DENIED);
				}

				AuthService._persistSessionToken(session, username);
				return AuthService._configureAuthenticatedSession(session);
			});
	},

	logout: () => {
		return api.client.delete('/session').then((response) => {
			AuthService.removeToken();
			window.location.href='/';
			return response?.data || response || {};
		});
	},

	become: async(userId) => {
		try {
			const current = AuthService.getToken();
			const isChaining = !!current?.isImpersonating;
			// When switching between impersonated users, the API's admin check runs against
			// the current (impersonated) user and would reject. Re-auth with the original
			// admin token stashed in `actual`, and keep `actual` pinned there so we don't
			// nest impersonation tokens.
			const actual = isChaining ? current.actual : current;

			const config = isChaining
				? {
					auth: {
						username: actual.username,
						password: `token-${actual.token}`
					}
				}
				: {};

			const result = await api.client.patch('/session', { _id: userId }, config);

			if(!result?.data?.username || !result?.data?.token) {
				throw new Error('User not found');
			}

			const session = result?.data || {};
			await AuthService.removeToken();
			await AuthService.setToken({
				isImpersonating: true,
				actual,
				username: session.username,
				token: session.token
			});

			window.location.reload();
		}
		catch{
			window.location.reload();
		}
	},

	isImpersonating: () => {
		return AuthService.getToken().isImpersonating || false;
	},

	switchBack: async() => {
		const username = AuthService.getToken()?.actual?.username || null;
		const token = AuthService.getToken()?.actual?.token || null;

		if(username && token) {
			await AuthService.setToken({
				isImpersonating: false,
				username,
				token
			});
		}
		else {
			await AuthService.removeToken();
		}
		Cookies.remove('pixwel.user.original');
		window.location.reload();
	},

	resetPassword: async({ token, password }) => {
		await api.client.post('/password-reset', { token, password });
	},

	requestResetPassword({ email, url }) {
		return api.client.post('/password-reset', {
			email,
			url: decodeURIComponent(url)
		});
	},

	/**
	 * What to ask this address for.
	 *
	 * One bit, and a deliberately uninformative one: an address with no account answers exactly
	 * as an ordinary account without a passkey does.
	 */
	signInMethods: ({ identifier }) => {
		return api.client.post('/session/methods', { identifier }, { validateStatus: () => true })
			.then(({ data, status } = {}) => {
				if(status !== 200) {
					throw new Error(data?.error || 'Could not continue right now');
				}

				// `passkeyRequired` only ever comes back true alongside a passkey the account
				// holds — see `Session::methods()` — so the screen can treat it as "this one,
				// and nothing else" rather than having to ask what else is allowed.
				//
				// `password` is whether one would be *accepted*, not whether the account has
				// one: the API answers it for an address with no account too, so that the
				// negative case stays indistinguishable. It is already false wherever
				// `passkeyRequired` is true.
				return {
					passkey: !!data?.passkey,
					passkeyRequired: !!data?.passkeyRequired,
					password: !!data?.password
				};
			});
	},

	/**
	 * Asks for a one-time sign-in link.
	 *
	 * The return URL is sent because the API refuses to build a link pointing anywhere but one
	 * of its own configured origins — it will not mail somebody a real token aimed at a site
	 * that is not ours.
	 */
	requestSignInLink: ({ identifier }) => {
		const url = `${window.location.origin}/sign-in/:token`;

		return api.client.post('/session/link', { identifier, url }, { validateStatus: () => true })
			.then(({ data, status } = {}) => {
				if(status !== 200) {
					throw new Error(data?.error || 'Could not send a link right now');
				}

				return { sent: true };
			});
	},

	/**
	 * Asks what an invitation is for, without spending it.
	 *
	 * Two things the registration screen needs before it draws anything: whose invitation this
	 * is, and whether the account may hold a password at all. Spending the token to find out
	 * would hand it to the first mail scanner that prefetches the URL.
	 */
	describeInvite: ({ token }) => {
		return api.client.post('/session/invite', { token }, { validateStatus: () => true })
			.then(({ data, status } = {}) => {
				if(status !== 200) {
					throw new Error(status >= 500
						? 'Invitations are not available right now'
						: (data?.error || 'That invitation link is no longer valid'));
				}

				return {
					email: data?.email || '',
					name: data?.name || '',
					territory: data?.territory || '',
					// An invitation mailed before links carried a token. It cannot be accepted;
					// the screen offers a sign-in link to the address instead.
					legacy: !!data?.legacy,
					passkeyRequired: !!data?.passkeyRequired,
					// Whether a password may be set at all -- separate from the requirement, since
					// passwords can be off without a passkey being required. Left `undefined`
					// when an older API does not say, so the form falls back to the requirement
					// rather than reading "no answer" as "no password".
					passwordAllowed: typeof data?.passwordAllowed === 'boolean' ? data.passwordAllowed : undefined
				};
			});
	},

	/**
	 * Spends an invitation for a session.
	 *
	 * This is where the address is proved -- the invitation link carries a real token, so
	 * accepting it stands where the old password step did. Everything after this is done as an
	 * authenticated person, including writing their own name.
	 */
	acceptInvite: ({ token }) => {
		return api.client.post('/session/accept', { token }, { validateStatus: () => true })
			.then(({ data, status } = {}) => {
				if(status !== 200) {
					throw new Error(status >= 500
						? 'Invitations are not available right now'
						: (data?.error || AUTH_ERRORS.ACCESS_DENIED));
				}

				// An invited account that already holds an authenticator -- re-invited, or
				// enrolled between the invitation and the click -- is challenged rather than
				// let in, exactly as `enterWithLink` is: the invitation proves the mailbox and
				// nothing else. Read before the token, since a challenge carries one too, and
				// persisting that as a session would leave registration writing to an account
				// it never got (`_id` is absent) after the one-time invitation was spent.
				if(data?.twofactor) {
					return AuthService._challengeFrom(data);
				}

				if(!data?.token) {
					throw new Error(data?.error || AUTH_ERRORS.ACCESS_DENIED);
				}

				AuthService._persistSessionToken(data, data.emailNormal);

				return AuthService._configureAuthenticatedSession(data);
			});
	},

	/**
	 * The half-finished sign-in an emailed link or an accepted invitation answers with when the
	 * account holds an authenticator: the challenge token, how many recovery codes are left,
	 * and whether a passkey is owed once the code is proved -- carried through the challenge
	 * because a challenge is no session, and there is no other way for the next screen to know.
	 */
	_challengeFrom: (data) => ({
		twoFactor: {
			token: data.token,
			recoveryCodes: data.recoveryCodes ?? null,
			passkeyRequired: !!data.passkeyRequired
		}
	}),

	/**
	 * Spends a one-time sign-in link for a session.
	 *
	 * `passkeyRequired` comes back when the account has none, which is how the screen after this
	 * knows to insist on one.
	 */
	enterWithLink: ({ token }) => {
		return api.client.post('/session/enter', { token }, { validateStatus: () => true })
			.then(({ data, status } = {}) => {
				if(status !== 200) {
					throw new Error(status >= 500
						? 'Sign-in is not available right now'
						: (data?.error || AUTH_ERRORS.ACCESS_DENIED));
				}

				// An account with an authenticator is challenged rather than let in: a link is
				// one factor, and the second one is not waived for whoever asked for a link.
				// Checked before the token, because a challenge carries one too — and spending
				// that as a session token would be reading it as the thing it is not.
				if(data?.twofactor) {
					return AuthService._challengeFrom(data);
				}

				if(!data?.token) {
					throw new Error(data?.error || AUTH_ERRORS.ACCESS_DENIED);
				}

				AuthService._persistSessionToken(data, data.emailNormal);

				return AuthService._configureAuthenticatedSession(data);
			});
	},

	/**
	 * Exchanges a signed passkey assertion for an ordinary Pixwel session.
	 *
	 * The ceremony is already done by the time this runs: the authenticator has signed a
	 * challenge the API issued, and nothing here can tell whether it checked out. That is the
	 * API's job, and a refusal is read from the status rather than thrown at the shared
	 * interceptor — which treats any 401 as an expired session and reloads the page, wiping the
	 * message before anyone could read it.
	 */
	passkeyLogin: ({ ceremonyId, credential }) => {
		return api.client.post('/session/passkey', { ceremonyId, credential }, {
			validateStatus: () => true
		})
			.then(({ data, status } = {}) => {
				if(status !== 200 || !data?.token) {
					throw new Error(status >= 500
						? 'Passkey sign-in is not available right now'
						: (data?.error || AUTH_ERRORS.ACCESS_DENIED));
				}

				AuthService._persistSessionToken(data, data.emailNormal);

				return AuthService._configureAuthenticatedSession(data);
			});
	},

	/**
	 * Completes a two-factor sign-in.
	 *
	 * Takes the challenge token, plus a code when the person has an authenticator. The emailed
	 * link carries only a token, and the API accepts that on its own for anyone not enrolled —
	 * which is why the code is conditional here rather than required.
	 *
	 * @param {Object|string} input `{ token, code, recoveryCode }`, or a bare token.
	 * @returns {Promise<Object>} `{ data }`, matching what the caller expects of a response.
	 */
	twoFactorLogin: (input) => {
		const { token, code, recoveryCode } = typeof input === 'string' ? { token: input } : (input || {});

		const body = { token };

		// A recovery code is sent instead of a code, never as a retry after one fails, so a
		// mistyped digit cannot quietly spend one of the ten.
		if(recoveryCode) {
			body.recoveryCode = recoveryCode;
		}
		else if(code) {
			body.code = code;
		}

		return api.client.post('/session', body, {
			// The shared interceptor treats any 401 as an expired session and reloads the page,
			// which would throw away the half-finished sign-in every time somebody mistypes a
			// digit. The API answers 422 for a wrong code for the same reason; this covers the
			// rest, including the pre-existing 401s for a spent or expired token.
			validateStatus: () => true
		})
			.then(({ data, status } = {}) => {
				if(status !== 200 || !data?.token) {
					const failure = new Error(status >= 500
						? 'Sign-in is not available right now'
						: (data?.error || AUTH_ERRORS.ACCESS_DENIED));

					// So a caller can tell a dead challenge (401 expired or torn down, 429 too
					// many attempts) from a wrong code (422), and send the person back to start
					// again rather than leaving them on a prompt that can never succeed.
					failure.status = status;

					throw failure;
				}

				return { data };
			});
	},

	/**
	 * Spend a two-factor challenge and hand back what `login` needs to finish.
	 *
	 * The shape of `POST /session`'s answer -- where the address and the token live -- was
	 * hand-typed at three call sites. It lives here once. Deliberately stops short of calling
	 * `login` itself: the modal signs in through the `useAuth` mutation so its state follows,
	 * while `pages/sign-in` calls `AuthService.login` directly and then redirects, and a helper
	 * that picked one would break the other.
	 */
	completeTwoFactor: async({ token, ...proof }) => {
		const verified = await AuthService.twoFactorLogin({ token, ...proof });

		return {
			username: verified?.data?.emailNormal || verified?.data?.email || '',
			token: verified?.data?.token ?? '',
			isTwoFactor: true
		};
	},

	removeToken: () => {
		Cookies.remove(STORE_KEY);
	},

	resumeSession: async() => {
		const token = AuthService.getToken();

		if(typeof token === 'string' && token.startsWith('ey')) {
			let session = await api.client
				.get('/session', {
					headers: {
						Authorization: token
					}
				})
				.then((res) => res.data);
			return session;
		}

		if(!token) {
			throw new Error(AUTH_ERRORS.NO_TOKEN);
		}

		return await AuthService.login({
			username: token.username,
			token: token.token
		});
	},
	isJWT(tok) {
		const token = tok || this.getToken();
		return typeof token === 'string' && token.startsWith('ey');
	},
	getToken: () => {
		const clerkToken = Cookies.getJSON(CLERK_STORE_KEY);
		if(clerkToken) {
			return clerkToken;
		}
		const token = Cookies.getJSON(STORE_KEY);
		if(!token || !token.username || !token.token) {
			return false;
		}
		return token;
	},
	setToken: (credentials) => {
		return Cookies.set(STORE_KEY, credentials);
	}
};

export default AuthService;
